<template>
  <div class="product-edit-container">
    <el-card shadow="never" class="main-card">
      <div slot="header" class="card-header">
        <i class="el-icon-goods title-icon"></i>
        <span>添加商品</span>
      </div>

      <el-form ref="productFormRef" :model="productForm" :rules="productRules" label-width="100px" style="padding: 0 20px;">
        <!-- 基本信息 -->
        <div class="section-title">基本信息</div>
        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="商品名称" prop="name">
              <el-input v-model="productForm.name" placeholder="请输入商品名称" maxlength="128" show-word-limit />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="商品分类" prop="categoryId">
              <el-select v-model="productForm.categoryId" placeholder="请选择商品分类" style="width:100%;">
                <el-option v-for="c in categoryList" :key="c.id" :label="c.name" :value="c.id" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="24">
          <el-col :span="12">
            <el-form-item label="排序序号">
              <el-input-number v-model="productForm.sortOrder" :min="0" :max="9999" style="width:100%;" />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 价格与库存 -->
        <div class="section-title">价格与库存</div>
        <el-row :gutter="24">
          <el-col :span="8">
            <el-form-item label="售价(元)" prop="price">
              <el-input-number v-model="productForm.price" :min="0" :precision="2" :step="0.01" style="width:100%;" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="原价(元)" prop="originalPrice">
              <el-input-number v-model="productForm.originalPrice" :min="0" :precision="2" :step="0.01" style="width:100%;" />
            </el-form-item>
          </el-col>
          <el-col :span="8">
            <el-form-item label="库存" prop="stock">
              <el-input-number v-model="productForm.stock" :min="0" :max="999999" style="width:100%;" />
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 状态 -->
        <div class="section-title">商品状态</div>
        <el-form-item label="上架状态">
          <el-radio-group v-model="productForm.status">
            <el-radio-button :label="1">上架</el-radio-button>
            <el-radio-button :label="2">下架</el-radio-button>
          </el-radio-group>
        </el-form-item>

        <!-- 描述 -->
        <div class="section-title">商品描述</div>
        <el-form-item label="商品描述">
          <el-input v-model="productForm.description" type="textarea" :rows="3" placeholder="请输入商品描述" maxlength="500" show-word-limit />
        </el-form-item>

        <!-- 图片 -->
        <div class="section-title">商品图片</div>
        <el-form-item label="商品主图">
          <div class="upload-row">
            <div class="upload-box" @click="openImageSelector('main')">
              <template v-if="mainImageUrl">
                <img :src="mainImageUrl" class="upload-thumb" />
                <div class="upload-mask"><i class="el-icon-delete" @click.stop="removeMainImage"></i></div>
              </template>
              <template v-else>
                <i class="el-icon-plus upload-icon"></i>
                <span>上传主图</span>
              </template>
            </div>
          </div>
        </el-form-item>

        <el-form-item label="详情图片">
          <div class="upload-row">
            <div v-for="(img, index) in contentImageList" :key="index" class="upload-box">
              <img :src="img.url" class="upload-thumb" />
              <div class="upload-mask"><i class="el-icon-delete" @click="removeContentImage(index)"></i></div>
            </div>
            <div v-if="contentImageList.length < 5" class="upload-box" @click="openImageSelector('content')">
              <i class="el-icon-plus upload-icon"></i>
              <span>上传图片</span>
            </div>
          </div>
          <div class="upload-tip">最多5张，点击图片可删除</div>
        </el-form-item>

        <!-- 按钮 -->
        <el-form-item style="margin-top: 40px; text-align: center;">
          <el-button @click="handleCancel">返回列表</el-button>
          <el-button type="primary" @click="handleSubmit" :loading="submitting" style="margin-left: 16px;">立即创建</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <image-selector v-model="showImageSelector" :max-count="currentSelectMax" @select="handleImageSelect" />
  </div>
</template>

<script>
import api from "@/utils/api"
import ImageSelector from '@/components/ImageSelector'

export default {
  name: 'ProductAdd',
  components: { ImageSelector },
  data() {
    return {
      submitting: false,
      storeId: null,
      showImageSelector: false,
      currentUploadField: '',
      currentSelectMax: 1,

      productForm: {
        name: '',
        categoryId: null,
        price: 0,
        originalPrice: 0,
        description: '',
        imageId: null,
        contentImageIds: '',
        stock: 0,
        sortOrder: 0,
        status: 1
      },

      mainImageUrl: '',
      contentImageList: [],

      productRules: {
        name: [{ required: true, message: '请输入商品名称', trigger: 'blur' }],
        categoryId: [{ required: true, message: '请选择商品分类', trigger: 'change' }],
        price: [{ required: true, message: '请输入售价', trigger: 'blur' }],
        originalPrice: [{ required: true, message: '请输入原价', trigger: 'blur' }],
        stock: [{ required: true, message: '请输入库存', trigger: 'blur' }]
      },

      categoryList: []
    }
  },

  mounted() {
    this.storeId = this.$route.query.merchantId
    if (!this.storeId) {
      const adminInfo = this.$store.state.adminInfo || {}
      this.storeId = adminInfo.merchantId
    }
    this.loadCategoryList()
  },

  methods: {
    async loadCategoryList() {
      try {
        const res = await api.product_getCategoryList({}, null)
        const data = res.data || res
        if (data.code === '000') {
          this.categoryList = data.data.list || data.data || []
        }
      } catch (e) {}
    },

    getImageUrl(id) {
      return id ? `${process.env.VUE_APP_BASE_API}/image/getImage/${id}` : ''
    },

    openImageSelector(field) {
      this.currentUploadField = field
      this.currentSelectMax = field === 'main' ? 1 : 5 - this.contentImageList.length
      if (this.currentSelectMax <= 0) { this.$message.warning('最多5张'); return }
      this.showImageSelector = true
    },

    handleImageSelect(images) {
      if (!images.length) return
      if (this.currentUploadField === 'main') {
        this.productForm.imageId = images[0].id
        this.mainImageUrl = images[0].url
      } else {
        this.contentImageList.push(...images.map(i => ({ id: i.id, url: i.url })))
        this.productForm.contentImageIds = this.contentImageList.map(i => i.id).join(',')
      }
    },

    removeMainImage() {
      this.productForm.imageId = null
      this.mainImageUrl = ''
    },

    removeContentImage(index) {
      this.contentImageList.splice(index, 1)
      this.productForm.contentImageIds = this.contentImageList.map(i => i.id).join(',')
    },

    async handleSubmit() {
      try {
        await this.$refs.productFormRef.validate()
        this.submitting = true

        const submitData = {
          name: this.productForm.name,
          categoryId: this.productForm.categoryId,
          price: Math.round(this.productForm.price * 100),
          originalPrice: Math.round(this.productForm.originalPrice * 100),
          description: this.productForm.description,
          imageId: this.productForm.imageId,
          contentImageIds: this.productForm.contentImageIds,
          stock: this.productForm.stock,
          sortOrder: this.productForm.sortOrder,
          status: this.productForm.status
        }

        const res = await api.product_addProduct(submitData)
        const data = res.data || res
        if (data.code === '000') {
          this.$message.success('添加成功')
          this.$router.push('/Index/Product/List')
        } else {
          this.$message.error(data.message || '添加失败')
        }
      } catch (e) {
        if (e !== 'cancel') this.$message.error('添加失败')
      } finally {
        this.submitting = false
      }
    },

    handleCancel() {
      this.$router.push('/Index/Product/List')
    }
  }
}
</script>

<style scoped lang="scss">
.product-edit-container { padding: 24px; max-width: 900px; margin: 0 auto; }
.main-card { border-radius: 8px; overflow: hidden; }
.card-header { font-size: 18px; font-weight: 600; color: #303133; display: flex; align-items: center; gap: 8px; }
.title-icon { color: #409eff; font-size: 20px; }
.section-title { font-size: 14px; font-weight: 600; color: #303133; padding: 16px 0 12px; margin-bottom: 8px; border-bottom: 1px solid #f0f0f0; &:first-child { padding-top: 0; } }

.upload-row { display: flex; flex-wrap: wrap; gap: 12px; }
.upload-box {
  width: 100px; height: 100px; border: 1px dashed #d9d9d9; border-radius: 8px;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  cursor: pointer; position: relative; overflow: hidden; background: #fafafa; transition: border-color .3s;
  &:hover { border-color: #409eff; }
  .upload-icon { font-size: 24px; color: #999; margin-bottom: 4px; }
  span { font-size: 12px; color: #999; }
  .upload-thumb { width: 100%; height: 100%; object-fit: cover; }
  .upload-mask {
    position: absolute; top: 0; left: 0; width: 100%; height: 100%;
    background: rgba(0,0,0,.5); display: flex; align-items: center; justify-content: center;
    opacity: 0; transition: opacity .3s;
    i { color: #fff; font-size: 20px; }
  }
  &:hover .upload-mask { opacity: 1; }
}
.upload-tip { font-size: 12px; color: #999; margin-top: 8px; }
</style>